import { useEffect, useRef, useState } from "react";
import { htmlToMarkdown, markdownToHtml } from "../../shared/markdown";
import { useDebouncedAutosave, type AutosaveStatus } from "../../shared/useDebouncedAutosave";
import { downloadGeneratedFile, saveReport } from "./api";

interface Props {
  /** Latest known markdown body — re-seeded by `file_changed`/agent-done. */
  reportContent: string;
  slug: string | null;
  fallbackSlug: string | null;
}

export function Step4Report({ reportContent, slug, fallbackSlug }: Props) {
  const previewRef = useRef<HTMLDivElement>(null);
  const lastSeededRef = useRef<string>("");
  const [status, setStatus] = useState<{ kind: AutosaveStatus; message?: string }>({ kind: "idle" });
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [docxDownloading, setDocxDownloading] = useState(false);

  const { schedule, flush } = useDebouncedAutosave({
    save: async () => {
      const target = slug ?? fallbackSlug;
      if (!target || !previewRef.current) return;
      const md = htmlToMarkdown(previewRef.current.innerHTML);
      await saveReport(target, md);
    },
    onStatus: (kind, message) => {
      setStatus({ kind, message });
      if (kind === "saved") window.setTimeout(() => setStatus({ kind: "idle" }), 2000);
    },
  });

  // Refresh preview when the agent rewrote the report on disk. Skip if the
  // user is editing right now or a save is already pending.
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (reportContent === lastSeededRef.current) return;
    lastSeededRef.current = reportContent;
    el.innerHTML = markdownToHtml(reportContent);
  }, [reportContent]);

  async function downloadMarkdown() {
    if (!previewRef.current) return;
    await flush();
    const md = htmlToMarkdown(previewRef.current.innerHTML);
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "result.md";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function downloadPdf() {
    if (!slug && !fallbackSlug) return;
    setPdfDownloading(true);
    try {
      await flush();
      const result = await downloadGeneratedFile(
        [slug, fallbackSlug],
        "pdf",
        "report",
        "result.pdf",
      );
      if (!result.ok) setStatus({ kind: "error", message: result.error });
    } finally {
      setPdfDownloading(false);
    }
  }

  async function downloadDocx() {
    if (!slug && !fallbackSlug) return;
    setDocxDownloading(true);
    try {
      await flush();
      const target = slug ?? fallbackSlug ?? "result";
      const result = await downloadGeneratedFile(
        [slug, fallbackSlug],
        "docx",
        "report",
        `result_${target}.docx`,
      );
      if (!result.ok) setStatus({ kind: "error", message: result.error });
    } finally {
      setDocxDownloading(false);
    }
  }

  function statusText(): string {
    if (status.kind === "saving") return "Speichern…";
    if (status.kind === "saved") return "Gespeichert";
    if (status.kind === "error") return "Fehler: " + (status.message ?? "");
    return "";
  }

  const canDownload = Boolean(slug ?? fallbackSlug);

  return (
    <section className="wizard-section" data-step="4">
      <h2 className="form-title">Bericht fertig</h2>
      <p className="form-description">
        Der Bericht wurde erstellt. Direkt bearbeiten; Änderungen werden automatisch gespeichert.
      </p>
      <div className="form-actions" style={{ marginBottom: "1rem" }}>
        <button type="button" className="btn btn-primary" onClick={() => void downloadMarkdown()}>
          Download Markdown
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void downloadPdf()}
          disabled={!canDownload || pdfDownloading}
        >
          {pdfDownloading ? "PDF wird erzeugt…" : "Download PDF"}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void downloadDocx()}
          disabled={!canDownload || docxDownloading}
        >
          {docxDownloading ? "Word wird erzeugt…" : "Download Word"}
        </button>
        <span
          className={`autosave-status${status.kind === "saving" ? " saving" : ""}${status.kind === "saved" ? " saved" : ""}`}
          aria-live="polite"
        >
          {statusText()}
        </span>
      </div>
      <div
        ref={previewRef}
        className="result-preview result-preview-editable"
        contentEditable
        spellCheck
        suppressContentEditableWarning
        data-placeholder="Bericht wird geladen…"
        onInput={() => schedule()}
      />
    </section>
  );
}
